import { useState } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import { deliveryTimeService } from '../services'

/**
 * Bulk delete tools for delivery time management.
 * Supports CSV (command column), comma-separated IDs, and per-org/location deletion.
 */
const DeliveryTimeBulkDeletePanel = ({ onDeleted }) => {
  const [idsInput, setIdsInput] = useState('')
  const [orgIdInput, setOrgIdInput] = useState('')
  const [locationIdInput, setLocationIdInput] = useState('')
  const [csvFile, setCsvFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [error, setError] = useState(null)

  const parseIds = (raw) =>
    String(raw || '')
      .split(/[,;\s]+/)
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0)

  const handleDownloadTemplate = async () => {
    try {
      setError(null)
      await deliveryTimeService.downloadBulkDeleteCsv()
      setMessage(
        'Downloaded CSV with all delivery times. Set command=delete on rows to remove, then upload.',
      )
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to download CSV')
    }
  }

  const handleCsvUpload = async () => {
    if (!csvFile) {
      setError('Select a CSV file first')
      return
    }
    try {
      setLoading(true)
      setError(null)
      const result = await deliveryTimeService.bulkDeleteFromCsv(csvFile)
      setMessage(
        result.message ||
          `Deleted ${result.deleted ?? 0} row(s); skipped ${result.skipped ?? 0}`,
      )
      setCsvFile(null)
      onDeleted?.()
    } catch (err) {
      setError(err.response?.data?.message || 'CSV bulk delete failed')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteIds = async () => {
    const ids = parseIds(idsInput)
    if (ids.length === 0) {
      setError('Enter one or more delivery time IDs (comma-separated)')
      return
    }
    if (
      !window.confirm(
        `Delete ${ids.length} delivery time configuration(s)? This cannot be undone.`,
      )
    ) {
      return
    }
    try {
      setLoading(true)
      setError(null)
      const result = await deliveryTimeService.bulkDeleteByIds(ids)
      setMessage(result.message || `Deleted ${result.deleted ?? 0} row(s)`)
      setIdsInput('')
      onDeleted?.()
    } catch (err) {
      setError(err.response?.data?.message || 'Bulk delete by IDs failed')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteByOrg = async () => {
    const orgId = parseInt(orgIdInput, 10)
    if (!Number.isFinite(orgId) || orgId < 1) {
      setError('Enter a valid Shop Id')
      return
    }
    if (
      !window.confirm(
        `Delete ALL delivery times for Shop Id ${orgId}? This cannot be undone.`,
      )
    ) {
      return
    }
    try {
      setLoading(true)
      setError(null)
      const result = await deliveryTimeService.bulkDeleteByOrganization(orgId)
      setMessage(result.message || `Deleted ${result.deleted ?? 0} row(s)`)
      onDeleted?.()
    } catch (err) {
      setError(err.response?.data?.message || 'Delete by Shop Id failed')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteByLocation = async () => {
    const locationId = parseInt(locationIdInput, 10)
    if (!Number.isFinite(locationId) || locationId < 1) {
      setError('Enter a valid location ID')
      return
    }
    if (
      !window.confirm(
        `Delete ALL delivery times for location ${locationId} (every shop)? This cannot be undone.`,
      )
    ) {
      return
    }
    try {
      setLoading(true)
      setError(null)
      const result = await deliveryTimeService.bulkDeleteByLocation(locationId)
      setMessage(result.message || `Deleted ${result.deleted ?? 0} row(s)`)
      onDeleted?.()
    } catch (err) {
      setError(err.response?.data?.message || 'Delete by location failed')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteByOrgLocation = async () => {
    const orgId = parseInt(orgIdInput, 10)
    const locationId = parseInt(locationIdInput, 10)
    if (!Number.isFinite(orgId) || orgId < 1 || !Number.isFinite(locationId) || locationId < 1) {
      setError('Enter valid Shop Id and location ID')
      return
    }
    if (
      !window.confirm(
        `Delete delivery time for Shop Id ${orgId} + location ${locationId}?`,
      )
    ) {
      return
    }
    try {
      setLoading(true)
      setError(null)
      const result = await deliveryTimeService.bulkDeleteByOrgAndLocation(
        orgId,
        locationId,
      )
      setMessage(result.message || `Deleted ${result.deleted ?? 0} row(s)`)
      onDeleted?.()
    } catch (err) {
      setError(err.response?.data?.message || 'Delete by shop + location failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card sx={{ mb: 3, borderRadius: 2, border: '1px solid #ffcdd2' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <DeleteSweepIcon color="error" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Bulk Delete
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Download the CSV with all mappings, set <strong>command</strong> to{' '}
          <code>delete</code> for rows to remove, then upload. You can also delete
          by comma-separated IDs or wipe all times for a shop or location.
        </Typography>

        {message && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            disabled={loading}
          >
            Download delete CSV
          </Button>
          <Button variant="outlined" component="label" disabled={loading}>
            {csvFile ? csvFile.name : 'Choose CSV'}
            <input
              type="file"
              accept=".csv"
              hidden
              onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
            />
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
            onClick={handleCsvUpload}
            disabled={loading || !csvFile}
          >
            Upload & delete
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <TextField
            label="Delivery time IDs (comma-separated)"
            value={idsInput}
            onChange={(e) => setIdsInput(e.target.value)}
            size="small"
            sx={{ minWidth: 280, flex: 1 }}
            placeholder="e.g. 12, 45, 78"
            disabled={loading}
          />
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteIds}
            disabled={loading}
          >
            Delete IDs
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', mt: 2 }}>
          <TextField
            label="Shop Id"
            value={orgIdInput}
            onChange={(e) => setOrgIdInput(e.target.value)}
            size="small"
            type="number"
            disabled={loading}
          />
          <TextField
            label="Location ID (optional — pair delete)"
            value={locationIdInput}
            onChange={(e) => setLocationIdInput(e.target.value)}
            size="small"
            type="number"
            disabled={loading}
          />
          <Button
            variant="outlined"
            color="error"
            onClick={handleDeleteByOrg}
            disabled={loading}
          >
            Delete all for shop
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDeleteByLocation}
            disabled={loading}
          >
            Delete all for location
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteByOrgLocation}
            disabled={loading}
          >
            Delete shop + location
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}

export default DeliveryTimeBulkDeletePanel
